import { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { SELF_JOIN_CHECK_PATH } from '../constants/api';
import { AppConfig } from '../types/app';
import { profileRequest } from '../utils/apiHelper';
import logger from '../utils/logger';

export interface SelfJoinCheckResponse {
  isActive: boolean;
  name?: string;
}

export async function checkSelfJoinLink(
  config: AppConfig,
  orgId: string
): Promise<SelfJoinCheckResponse> {
  const fetchOpts: RequestInit = {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'X-Correlation-ID': uuidv4(),
    },
    body: JSON.stringify({ orgId }),
  };
  const options = {
    metricNs: 'SelfJoinCheck',
    logWithRum: true,
    logWithKatal: true,
  };
  const response = await profileRequest(
    config,
    SELF_JOIN_CHECK_PATH,
    fetchOpts,
    options
  );

  return await response.json();
}

function useSelfJoinCheck(config: AppConfig, orgId: string | undefined) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [result, setResult] = useState<SelfJoinCheckResponse | undefined>();

  useEffect(() => {
    if (!orgId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(undefined);
    checkSelfJoinLink(config, orgId)
      .then((response) => {
        if (!cancelled) setResult(response);
      })
      .catch((err) => {
        logger.debug(err);
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // config is stable for the lifetime of the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  return {
    isLoading,
    error,
    // Anything other than an explicit active link with a name counts as inactive.
    isActive: result?.isActive === true && Boolean(result.name),
    orgName: result?.name,
  };
}

export default useSelfJoinCheck;
